import { Link } from 'react-router-dom'

import { PRIVACY_UPDATED, PRIVACY_VERSION, formatLegalDate, privacyCopy } from '../content/privacy'
import { SUPPORT_EMAIL } from '../content/site'
import { TERMS_UPDATED, TERMS_VERSION, termsCopy } from '../content/terms'
import { PAGES, type Lang } from '../i18n'
import styles from './LegalPage.module.css'

/**
 * The privacy notice and the terms. One component, because they are the same page with
 * different sections, and the pair cross-link to each other at the bottom.
 *
 * The version and the date come from the two constants rather than from a sentence in
 * the copy, so the line a reader would quote back at us cannot drift from the value the
 * sitemap and seo.ts publish. formatLegalDate spells the month out from a table instead
 * of using Intl: this renders once in Node during the prerender and again in the browser
 * on hydration, and a locale table that differed between the two would be a hydration
 * mismatch in the one line of a legal document that has to be exact.
 */
export default function LegalPage({ lang, doc }: { lang: Lang; doc: 'privacy' | 'terms' }) {
  const privacy = doc === 'privacy'
  const t = privacy ? privacyCopy[lang] : termsCopy[lang]
  const version = privacy ? PRIVACY_VERSION : TERMS_VERSION
  const updated = privacy ? PRIVACY_UPDATED : TERMS_UPDATED
  const other = privacy ? PAGES.terms[lang] : PAGES.privacy[lang]

  return (
    <article className={`container--reading ${styles.article}`}>
      <Link to={PAGES.home[lang]} className={styles.back}>
        {t.backLabel}
      </Link>

      <h1 className={styles.title}>{t.pageTitle}</h1>
      <p className={styles.updated}>
        {t.versionWord} {version} · {t.updatedWord} {formatLegalDate(updated, lang)}
      </p>
      <p className={styles.lede}>{t.lede}</p>

      {t.sections.map((section) => (
        <section key={section.title} className={styles.section}>
          <h2 className={styles.sectionTitle}>{section.title}</h2>
          <p className={styles.body}>{section.body}</p>
          {section.points && (
            <ul className={styles.points}>
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <div className={styles.contact}>
        <h2 className={styles.contactTitle}>{t.contactTitle}</h2>
        <p className={styles.contactBody}>{t.contactBody}</p>
        {/* The controller block. Empty facts are filtered out in orgLines, so this never
            prints a blank line while the street address is still to be confirmed. */}
        {t.contactLines.map((line) => (
          <p key={line} className={styles.contactLine}>
            {line}
          </p>
        ))}
        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.contactLink}>
          {SUPPORT_EMAIL}
        </a>
      </div>

      <Link to={other} className={styles.cross}>
        {t.crossLinkLabel}
      </Link>
    </article>
  )
}
